import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  CloudSun, Wind, Droplets, Eye, Cloud, Activity, Thermometer,
  RefreshCw, CheckCircle2, AlertTriangle, XCircle, MapPin, Compass, Zap, Loader2, Radio
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '/api';

const VERDICT_CONFIG = {
  BOM: {
    icon: CheckCircle2,
    label: 'BOM PARA VOAR',
    bg: 'bg-green-50',
    border: 'border-green-300',
    text: 'text-green-800',
    pill: 'bg-green-600 text-white',
    accent: 'text-green-600',
  },
  ATENCAO: {
    icon: AlertTriangle,
    label: 'ATENÇÃO - VOAR COM CUIDADO',
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    text: 'text-amber-800',
    pill: 'bg-amber-500 text-white',
    accent: 'text-amber-600',
  },
  NAO_RECOMENDADO: {
    icon: XCircle,
    label: 'NÃO RECOMENDADO',
    bg: 'bg-red-50',
    border: 'border-red-300',
    text: 'text-red-800',
    pill: 'bg-red-600 text-white',
    accent: 'text-red-600',
  },
};

// KP Index classification for drone operations
const getKpStatus = (kp) => {
  if (kp == null) {
    return {
      level: 'N/D',
      bandLabel: 'Indisponível',
      bg: 'bg-gray-900',
      ring: 'ring-gray-400',
      text: 'text-gray-100',
      accent: 'bg-gray-500',
      impact: 'Dado indisponível no momento - verifique NOAA SWPC manualmente antes do voo.',
      dotColor: 'bg-gray-400',
    };
  }
  if (kp < 4) {
    return {
      level: 'G0',
      bandLabel: 'Tranquilo',
      bg: 'bg-gradient-to-br from-emerald-600 to-green-700',
      ring: 'ring-emerald-300',
      text: 'text-white',
      accent: 'bg-emerald-400',
      impact: 'GPS e magnetômetro ESTÁVEIS. Operação normal permitida, sem restrições geomagnéticas.',
      dotColor: 'bg-emerald-300',
    };
  }
  if (kp < 5) {
    return {
      level: 'G1',
      bandLabel: 'Atenção',
      bg: 'bg-gradient-to-br from-yellow-500 to-amber-600',
      ring: 'ring-yellow-300',
      text: 'text-white',
      accent: 'bg-yellow-300',
      impact: 'Atenção a GPS/bússola. Pode haver flutuação leve. Monitorar telemetria de perto.',
      dotColor: 'bg-yellow-300',
    };
  }
  if (kp < 6) {
    return {
      level: 'G2',
      bandLabel: 'Tempestade Menor',
      bg: 'bg-gradient-to-br from-orange-500 to-red-600',
      ring: 'ring-orange-300',
      text: 'text-white',
      accent: 'bg-orange-300',
      impact: 'ALERTA - GPS degradado. Voos RTK podem perder FIX. Evite BVLOS e longas distâncias.',
      dotColor: 'bg-orange-300',
    };
  }
  if (kp < 7) {
    return {
      level: 'G3',
      bandLabel: 'Tempestade Moderada',
      bg: 'bg-gradient-to-br from-red-600 to-rose-700',
      ring: 'ring-red-300',
      text: 'text-white',
      accent: 'bg-red-300',
      impact: 'CRÍTICO - GPS/magnetômetro comprometidos. Voo NÃO RECOMENDADO. Aguarde KP baixar.',
      dotColor: 'bg-red-300',
    };
  }
  return {
    level: kp < 8 ? 'G4' : 'G5',
    bandLabel: kp < 8 ? 'Tempestade Forte' : 'Tempestade Extrema',
    bg: 'bg-gradient-to-br from-rose-700 to-fuchsia-900',
    ring: 'ring-rose-300',
    text: 'text-white',
    accent: 'bg-rose-300',
    impact: 'EXTREMO - NÃO VOAR. Tempestade geomagnética severa. GPS/bússola totalmente inoperantes.',
    dotColor: 'bg-rose-300',
  };
};

const WeatherBriefingWidget = ({ token, defaultLocation = '' }) => {
  const [location, setLocation] = useState(defaultLocation);
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchBriefing = useCallback(async (query) => {
    const target = (query ?? location).trim();
    if (!target) {
      setError('Informe uma cidade para o briefing');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/ai/weather-briefing`,
        { location: target },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBriefing(data);
      setLastUpdate(new Date());
      toast.success(`Briefing atualizado: ${data.location?.label || target}`);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao obter briefing meteorológico';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [token, location]);

  // Auto-fetch when default location is provided
  useEffect(() => {
    if (defaultLocation && !briefing && !loading) {
      setLocation(defaultLocation);
      fetchBriefing(defaultLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultLocation]);

  const useGeolocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocalização não suportada neste navegador');
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await axios.post(
            `${API_URL}/ai/weather-briefing`,
            { latitude: pos.coords.latitude, longitude: pos.coords.longitude },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setBriefing(data);
          setLastUpdate(new Date());
          setLocation(data.location?.label || '');
          toast.success('Briefing da sua localização atual');
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Erro ao obter briefing');
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        setLoading(false);
        toast.error('Não foi possível obter localização: ' + err.message);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  const verdict = briefing?.verdict;
  const vCfg = verdict ? VERDICT_CONFIG[verdict.verdict] : null;
  const VIcon = vCfg?.icon;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="weather-briefing-widget">
      {/* Header */}
      <div className="bg-gradient-to-r from-navy to-steel-blue p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gold/20 flex items-center justify-center">
            <CloudSun size={22} className="text-gold" />
          </div>
          <div>
            <h3 className="text-white font-rajdhani font-semibold uppercase tracking-wider text-sm">
              BRIEFING METEOROLÓGICO
            </h3>
            <p className="text-gray-300 text-xs font-mono">
              Jorginho IA · Open-Meteo + NOAA SWPC
            </p>
          </div>
        </div>
        {briefing && !loading && (
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/10 h-8 px-3"
            onClick={() => fetchBriefing()}
            data-testid="weather-refresh-btn"
          >
            <RefreshCw size={14} className="mr-1" />
            Atualizar
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-100 bg-gray-50/50">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-gray-600 font-mono text-xs uppercase flex items-center gap-1 mb-1">
              <MapPin size={12} /> Cidade / Localização
            </Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchBriefing()}
              placeholder="Ex: Salvador, Manaus, Rio de Janeiro..."
              className="input-hud"
              data-testid="weather-location-input"
            />
          </div>
          <Button
            onClick={() => fetchBriefing()}
            disabled={loading || !location.trim()}
            className="btn-primary"
            data-testid="weather-fetch-btn"
          >
            {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CloudSun size={16} className="mr-2" />}
            Obter Briefing
          </Button>
          <Button
            onClick={useGeolocation}
            disabled={loading}
            variant="outline"
            className="border-gray-300"
            title="Usar minha localização"
            data-testid="weather-geo-btn"
          >
            <Compass size={16} />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {!briefing && !loading && !error && (
          <div className="text-center py-8 text-gray-400">
            <CloudSun size={48} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm font-mono">
              Digite uma cidade ou use sua localização para o briefing operacional RPAS
            </p>
          </div>
        )}

        {loading && !briefing && (
          <div className="text-center py-8 text-gray-500">
            <Loader2 size={32} className="mx-auto mb-3 animate-spin text-gold" />
            <p className="text-sm font-mono">Consultando condições meteorológicas...</p>
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm font-mono">
            {error}
          </div>
        )}

        {briefing && verdict && vCfg && (() => {
          const kpValue = briefing.kp_index?.kp;
          const kpStatus = getKpStatus(kpValue);
          return (
          <div className="space-y-4">
            {/* Verdict banner */}
            <div className={`${vCfg.bg} ${vCfg.border} border-2 rounded-xl p-4`} data-testid="weather-verdict-banner">
              <div className="flex items-start gap-3">
                <VIcon size={32} className={vCfg.accent} />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`${vCfg.pill} px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider`}>
                      {vCfg.label}
                    </span>
                    <span className="text-xs font-mono text-gray-500">
                      {briefing.location?.label}
                    </span>
                  </div>
                  <p className={`${vCfg.text} text-sm font-semibold mt-2`}>
                    {verdict.summary}
                  </p>
                  {verdict.reasons_bad?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {verdict.reasons_bad.map((r, i) => (
                        <li key={i} className="text-xs text-red-700 flex items-start gap-1">
                          <XCircle size={12} className="mt-0.5 shrink-0" /> {r}
                        </li>
                      ))}
                    </ul>
                  )}
                  {verdict.reasons_caution?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {verdict.reasons_caution.map((r, i) => (
                        <li key={i} className="text-xs text-amber-700 flex items-start gap-1">
                          <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {r}
                        </li>
                      ))}
                    </ul>
                  )}
                  {verdict.reasons_good?.length > 0 && verdict.verdict === 'BOM' && (
                    <ul className="mt-2 space-y-1">
                      {verdict.reasons_good.map((r, i) => (
                        <li key={i} className="text-xs text-green-700 flex items-start gap-1">
                          <CheckCircle2 size={12} className="mt-0.5 shrink-0" /> {r}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {/* PROMINENT KP INDEX - INDISPENSABLE FOR DRONE OPS */}
            <div
              className={`relative rounded-xl overflow-hidden ${kpStatus.bg} ${kpStatus.text} ring-2 ${kpStatus.ring} shadow-lg`}
              data-testid="weather-kp-card"
            >
              <div className="absolute inset-0 opacity-10 pointer-events-none">
                <div className="absolute -top-4 -right-4 w-32 h-32 rounded-full bg-white blur-3xl" />
                <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-white blur-2xl" />
              </div>
              <div className="relative p-4 flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className={`w-16 h-16 rounded-xl ${kpStatus.accent}/30 bg-white/15 backdrop-blur flex flex-col items-center justify-center border border-white/20`}>
                    <Radio size={20} className={kpStatus.text} />
                    <span className={`text-[10px] font-mono uppercase tracking-wider mt-0.5 ${kpStatus.text}/80`}>KP</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] bg-white/20 px-2 py-0.5 rounded-full">
                      ÍNDICE GEOMAGNÉTICO · INDISPENSÁVEL
                    </span>
                  </div>
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <span className="font-mono text-3xl font-bold leading-none">
                      {kpValue != null ? kpValue.toFixed(1) : 'N/D'}
                    </span>
                    <span className="text-xs font-bold uppercase tracking-wider opacity-95">
                      {kpStatus.level} · {kpStatus.bandLabel}
                    </span>
                  </div>
                  <p className="text-[12px] leading-snug mt-2 opacity-95">
                    <span className={`inline-block w-2 h-2 rounded-full ${kpStatus.dotColor} mr-1.5 align-middle`} />
                    {kpStatus.impact}
                  </p>
                  <p className="text-[10px] font-mono opacity-70 mt-1.5">
                    Fonte: {briefing.kp_index?.source || 'NOAA SWPC'} · Crítico para GPS/RTK e magnetômetro
                  </p>
                </div>
              </div>
            </div>

            {/* Main metrics grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricCard
                icon={Thermometer}
                label="Temperatura"
                value={`${briefing.temperature?.celsius?.toFixed(1) ?? '?'}°C`}
                sub={`Sens. ${briefing.temperature?.apparent_celsius?.toFixed(1) ?? '?'}°C · ${briefing.temperature?.humidity_pct ?? '?'}%`}
                color="text-orange-600 bg-orange-50"
              />
              <MetricCard
                icon={Wind}
                label="Vento"
                value={`${briefing.wind?.speed_kmh?.toFixed(1) ?? '?'} km/h`}
                sub={`${briefing.wind?.speed_knots?.toFixed(1) ?? '?'} kt · dir ${briefing.wind?.direction_deg ?? '?'}°`}
                color="text-blue-600 bg-blue-50"
                highlight={briefing.wind?.speed_kmh > 30}
              />
              <MetricCard
                icon={Zap}
                label="Rajada"
                value={`${briefing.wind?.gust_kmh?.toFixed(1) ?? '?'} km/h`}
                sub={`${briefing.wind?.gust_knots?.toFixed(1) ?? '?'} kt`}
                color="text-purple-600 bg-purple-50"
                highlight={briefing.wind?.gust_kmh > 40}
              />
              <MetricCard
                icon={Cloud}
                label="Nuvens"
                value={`${briefing.clouds?.cover_pct ?? 0}%`}
                sub={briefing.clouds?.classification || '-'}
                color="text-gray-600 bg-gray-50"
                highlight={['BKN', 'OVC'].includes(briefing.clouds?.classification)}
              />
              <MetricCard
                icon={Eye}
                label="Visibilidade"
                value={briefing.visibility?.kilometers != null ? `${briefing.visibility.kilometers.toFixed(1)} km` : 'N/A'}
                sub={briefing.visibility?.meters ? `${briefing.visibility.meters.toFixed(0)} m` : '-'}
                color="text-teal-600 bg-teal-50"
                highlight={briefing.visibility?.kilometers != null && briefing.visibility.kilometers < 5}
              />
              <MetricCard
                icon={Droplets}
                label="Precipitação"
                value={briefing.precipitation?.active ? `${briefing.precipitation.mm_per_hour.toFixed(1)} mm/h` : 'Sem chuva'}
                sub={briefing.precipitation?.active ? 'ATIVA' : 'OK'}
                color="text-cyan-600 bg-cyan-50"
                highlight={briefing.precipitation?.active}
              />
              <MetricCard
                icon={CloudSun}
                label="Pressão"
                value={`${briefing.pressure_hpa?.toFixed(0) ?? '?'} hPa`}
                sub={briefing.is_day ? 'Dia' : 'Noite'}
                color="text-yellow-600 bg-yellow-50"
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between text-xs text-gray-400 font-mono pt-2 border-t border-gray-100">
              <span>📡 {briefing.timestamp_brasilia}</span>
              {lastUpdate && (
                <span>Atualizado há {Math.round((Date.now() - lastUpdate.getTime()) / 1000)}s</span>
              )}
            </div>
            <div className="text-[10px] text-gray-400 font-mono">
              Fontes: {briefing.sources?.join(' · ')}
            </div>
          </div>
          );
        })()}
      </div>
    </div>
  );
};

const MetricCard = ({ icon: Icon, label, value, sub, color, highlight = false }) => (
  <div
    className={`rounded-lg border p-3 transition-all ${
      highlight
        ? 'bg-red-50 border-red-200 ring-1 ring-red-200'
        : 'bg-white border-gray-200'
    }`}
  >
    <div className="flex items-center gap-2 mb-1">
      <div className={`w-7 h-7 rounded-md flex items-center justify-center ${color}`}>
        <Icon size={14} />
      </div>
      <span className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">
        {label}
      </span>
    </div>
    <div className={`text-lg font-bold font-mono ${highlight ? 'text-red-700' : 'text-navy'}`}>
      {value}
    </div>
    {sub && <div className="text-xs text-gray-500 font-mono mt-0.5 truncate">{sub}</div>}
  </div>
);

export default WeatherBriefingWidget;
